import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { phoneNumberShowFormat } from "helpers/GeneralHelper";
import { headerSearchCandidates } from "model/CandidateModel";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 350;

const recordsFromHeaderSearchResponse = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const getResultKey = (item = {}, index = 0) =>
  String(item?.candidateClientId ?? item?.action ?? item?.email ?? index).trim();

const getCandidateName = (item = {}) =>
  String(
    item?.fullName ||
      item?.candidateName ||
      `${item?.firstName || ""} ${item?.lastName || ""}`.trim() ||
      item?.name ||
      "Candidate"
  ).trim();

const getCandidateEmail = (item = {}) =>
  String(item?.email || item?.emailAddress || "").trim();

const getCandidatePhone = (item = {}) => {
  const phone = String(item?.phone || item?.phoneNumber || item?.mobile || "").trim();
  return phone || phoneNumberShowFormat(item?.phone || item?.phoneNumber || "");
};

const getCandidateCompanyLine = (item = {}) =>
  String(item?.client || item?.clientName || "").trim();

const resolveCandidateActionPath = (item = {}) => {
  const action = String(item?.action ?? "").trim();
  if (!action) return "";
  if (/^https?:\/\//i.test(action)) return action;
  return action.startsWith("/") ? action : `/${action}`;
};

const HeaderGlobalSearch = ({ className = "", variant = "default" }) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmedQuery = query.trim();
  const showDropdown =
    isOpen && trimmedQuery.length >= MIN_SEARCH_LENGTH;

  const resetSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setActiveIndex(-1);
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const openCandidateCallDetails = useCallback((candidate = {}) => {
    const path = resolveCandidateActionPath(candidate);
    if (!path) return;
    window.open(path, "_blank", "noopener,noreferrer");
    resetSearch();
  }, [resetSearch]);

  useEffect(() => {
    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const seq = ++fetchSeqRef.current;
      setLoading(true);
      try {
        const response = await headerSearchCandidates({
          nameContains: trimmedQuery,
        });
        if (fetchSeqRef.current !== seq) return;
        setResults(recordsFromHeaderSearchResponse(response));
        setActiveIndex(-1);
        setIsOpen(true);
      } finally {
        if (fetchSeqRef.current === seq) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmedQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (event) => {
    if (!showDropdown || !results.length) {
      if (event.key === "Escape") {
        resetSearch();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) =>
        prev < results.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : results.length - 1
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      openCandidateCallDetails(results[activeIndex]);
      return;
    }

    if (event.key === "Escape") {
      resetSearch();
    }
  };

  const renderMetaLine = (iconClass, value) => {
    if (!value) return null;
    return (
      <div className="header-global-search__meta-line">
        <i className={`header-global-search__meta-icon ${iconClass}`} aria-hidden />
        <span className="header-global-search__meta-text">{value}</span>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`header-global-search header-global-search--${variant} ${className}`.trim()}
    >
      <div className="header-global-search__input-wrap">
        <i className="fa fa-search header-global-search__search-icon" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          className="header-global-search__input"
          placeholder="Search candidate..."
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (trimmedQuery.length >= MIN_SEARCH_LENGTH) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          aria-label="Search candidates"
          aria-expanded={showDropdown}
          aria-controls="header-global-search-results"
          autoComplete="off"
          spellCheck={false}
        />
        {query ? (
          <button
            type="button"
            className="header-global-search__clear"
            onClick={resetSearch}
            aria-label="Clear search"
          >
            <i className="fa fa-times-circle" aria-hidden />
          </button>
        ) : null}
      </div>

      {showDropdown ? (
        <div
          id="header-global-search-results"
          className="header-global-search__dropdown"
          role="listbox"
        >
          {loading ? (
            <div className="header-global-search__status">
              <i className="fa fa-spinner fa-spin me-2" aria-hidden />
              Searching...
            </div>
          ) : null}

          {!loading && results.length === 0 ? (
            <div className="header-global-search__status">
              No candidates found for &ldquo;{trimmedQuery}&rdquo;
            </div>
          ) : null}

          {!loading
            ? results.map((candidate, index) => {
                const resultKey = getResultKey(candidate, index);
                const name = getCandidateName(candidate);
                const email = getCandidateEmail(candidate);
                const phone = getCandidatePhone(candidate);
                const companyLine = getCandidateCompanyLine(candidate);
                const isActive = index === activeIndex;

                return (
                  <button
                    key={resultKey}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`header-global-search__result${
                      isActive ? " header-global-search__result--active" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => openCandidateCallDetails(candidate)}
                  >
                    <div className="header-global-search__result-name">{name}</div>
                    {renderMetaLine("fa fa-envelope", email)}
                    {renderMetaLine("fa fa-phone", phone)}
                    {renderMetaLine("fa fa-building", companyLine)}
                  </button>
                );
              })
            : null}
        </div>
      ) : null}
    </div>
  );
};

HeaderGlobalSearch.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(["default", "horizontal"]),
};

export default HeaderGlobalSearch;
